// 介錯の致命「執行」の寄り方 (2026-10-20・作者「執行のとき、プレイヤーへの近づき方が滑って見える」)。
//
//   前の版 (DEV ?execApproach=old): 溜めの 600ms の間、刃を掲げた絵 (sue_raise) のまま主人公の 55px 手前まで等速で寄る。
//     砲の弾で崩した時は 500px を超えて寄る = 立ち姿が床を 900px/秒で滑る。
//   今の版: いつもの前ステップ (沈み → 残像を引いて踏み込む → 着地で潰れて土煙) を溜めの頭で1歩踏み、着いてから刃を掲げる。
//     絵・残像・沈みと潰れ・土煙・風・足音は前ステップのものをそのまま使う (exec.step に一歩の状態を載せる)。
//     溜めの長さ・着く点・振り下ろしの時刻・当たりは前の版と同じ。掲げる区間だけが短くなる (寄る距離が長いほど短い)。
//   文書 = docs/research/2026-10-20-exec-finisher-approach.md
import type { BossAttackTimeline } from './boss_pose_timeline'
import type { ExecutionerStepState } from './executioner_weapon'

export type ExecApproach = 'step' | 'old'
let mode: ExecApproach = 'step'
export function setExecApproach(raw: string | null): void { mode = import.meta.env.DEV && raw === 'old' ? 'old' : 'step' }
export function execApproachMode(): ExecApproach { return mode }

/** 溜めの頭で決める、この回の一歩 (技の写しに載せる)。 */
export type FinisherStepPlan = { fromX: number; toX: number; crouchMs: number; travelMs: number; landMs: number }

export const FINISHER_STEP = {
  crouchMs: 70,
  landMs: 90,
  /**
   * 踏み込みの速さ (px/ms)。1コマ 43px = 崩れの間の「普段の踏み込み並み」(1コマ 45px 以下・tests/stun_cut.test.ts) に収まる上限。
   * いつもの前ステップ (1000px/秒) より速い = 決めに行く一歩。出だしからこの速さで進み、最後の decelMs で緩んで止まる。
   */
  travelPxPerMs: 2.6,
  decelMs: 60,
  minTravelMs: 70,
  /** 掲げる区間を 600 − (70 + 260 + 90) = 180ms 以上残す。これを超える距離 (約 600px 超) だけ速さの上限を超える。 */
  maxTravelMs: 260,
} as const

export function finisherStepPlan(fromX: number, toX: number): FinisherStepPlan {
  const S = FINISHER_STEP
  const travelMs = Math.max(S.minTravelMs, Math.min(S.maxTravelMs, Math.abs(toX - fromX) / S.travelPxPerMs + S.decelMs / 2))
  return { fromX, toX, crouchMs: S.crouchMs, travelMs, landMs: S.landMs }
}

/**
 * 一歩を踏むか。着く点が主人公と逆側 (主人公の 55px より内側で崩れを見た) なら踏まず、その場で掲げる。
 *   10-20 作者「距離が近いときに後ろに瞬間移動して前に詰めてきて」= 前ステップの絵のまま 70ms で後ろへ跳び、
 *   着地の行き過ぎ (overshootPx) でさらに後ろへ出てから前へ戻っていた。刃の形には後ろへの一歩の絵が無い。
 */
export function finisherStepWanted(anchorX: number, targetX: number, playerX: number): boolean {
  return Math.abs(targetX - anchorX) > 10 && (targetX - anchorX) * (playerX - anchorX) > 0
}

export function finisherStepTotalMs(p: FinisherStepPlan): number { return p.crouchMs + p.travelMs + p.landMs }

/** 等速で進み、最後の decelMs で緩んで止まる (0〜1)。出だしの1コマで跳ばない。 */
function travelProgress(t: number, travelMs: number): number {
  const T = travelMs
  const d = Math.min(FINISHER_STEP.decelMs, T / 2)
  const v = 1 / (T - d / 2)
  const c = Math.max(0, Math.min(T, t))
  if (c <= T - d) return v * c
  const u = c - (T - d)
  return v * (T - d) + v * u - (v * u * u) / (2 * d)
}

/** 溜めの経過 ms での位置と、前ステップの状態 (着き終えたら null)。 */
export function finisherStepAt(p: FinisherStepPlan, elapsedMs: number): { x: number; step: ExecutionerStepState | null } {
  const direction: -1 | 1 = p.toX < p.fromX ? -1 : 1
  const base = {
    kind: 'forward' as const, style: 'lunge' as const, fromX: p.fromX, toX: p.toX, settleMs: 0, direction,
    commitOnLand: false, travelMs: p.travelMs, crouchMs: p.crouchMs,
  }
  if (elapsedMs < p.crouchMs) return { x: p.fromX, step: { ...base, phase: 'crouch', elapsedMs } }
  const t = elapsedMs - p.crouchMs
  if (t < p.travelMs) {
    return { x: p.fromX + (p.toX - p.fromX) * travelProgress(t, p.travelMs), step: { ...base, phase: 'travel', elapsedMs: t } }
  }
  const l = t - p.travelMs
  if (l < p.landMs) return { x: p.toX, step: { ...base, phase: 'land', elapsedMs: l } }
  return { x: p.toX, step: null }
}

/** 刃を掲げ始める時刻 (溜めの経過 ms)。一歩が無ければ 0。執行の紫の炎はここから灯る (src/exec_fx.ts)。 */
export function finisherRaiseStartMs(atk: { finisherStep?: FinisherStepPlan } | null | undefined): number {
  return atk?.finisherStep ? finisherStepTotalMs(atk.finisherStep) : 0
}

const cache = new WeakMap<BossAttackTimeline, Map<string, BossAttackTimeline>>()

/**
 * 溜めの頭に一歩の絵 (沈み → 踏み込み → 着地) を足し、元の並びの溜めのキーを残りの区間へ詰める。
 * 振り下ろし・戻りのキーは触らない。chargeMs = 技の溜めの長さ。
 */
export function finisherStepTimeline(base: BossAttackTimeline, p: FinisherStepPlan, chargeMs: number): BossAttackTimeline {
  const total = finisherStepTotalMs(p)
  if (chargeMs <= 0 || total >= chargeMs) return base
  const key = `${p.crouchMs}|${p.travelMs}|${p.landMs}|${chargeMs}`
  let byPlan = cache.get(base)
  if (!byPlan) { byPlan = new Map(); cache.set(base, byPlan) }
  const hit = byPlan.get(key)
  if (hit) return hit
  const at = (ms: number) => ms / chargeMs
  const a = at(total)
  const still = { offset: { x: 0, y: 0 }, scale: { x: 1, y: 1 } }
  const step: BossAttackTimeline = [
    { phase: 'telegraph', at: 0, image: 'step_crouch', hold: true, ...still },
    { phase: 'telegraph', at: at(p.crouchMs), image: 'step_smear', hold: true, ...still },
    { phase: 'telegraph', at: at(p.crouchMs + p.travelMs), image: 'step_land', hold: true, ...still },
  ]
  const tele = base.filter((k) => k.phase === 'telegraph').map((k) => ({ ...k, at: a + k.at * (1 - a) }))
  const out = [...step, ...tele, ...base.filter((k) => k.phase !== 'telegraph')]
  byPlan.set(key, out)
  return out
}
